import { Select } from '@mantine/core';
import React, { useCallback, useMemo } from 'react';
import { JiraFieldData2 } from '../../../../api/endpoints/jira';
import { LabelValue } from '../../../../platform/types/LabelValue';
import { Flexer } from '../../../dashboard/elements/LayoutElements';

interface JiraOption {
    value: string;
    id: string;
}

interface JiraOptionWithChild extends JiraOption {
    child?: JiraOption;
}

interface OptionWithChildInputProps {
    field: JiraFieldData2;
    value?: JiraOptionWithChild;
    error?: boolean;
    onChange: (value: JiraOptionWithChild) => void;
    onSubmit: (value: JiraOptionWithChild) => void;
}

const useParentOptions = (field: JiraFieldData2) => {
    return useMemo(() => (field.allowedValues ?? []).map(x => LabelValue(x.value, x.id)), [field]);
};

const useChildOptions = (field: JiraFieldData2, value: JiraOption) => {
    return useMemo(() => {
        if (!value) return [];
        const parentDef = field.allowedValues.find(x => x.id === value.id);
        return (parentDef?.children ?? []).map(x => LabelValue(x.value, x.id));
    }, [field, value]);
};

const useFindOptionById = (field: JiraFieldData2) => {
    return useCallback((id: string) => {
        if (!id) return null;
        return field.allowedValues.flatMap(x => [x, ...(x.children ?? [])]).find(x => x.id === id);
    }, [field.allowedValues]);
};

export const JiraOptionWithChildInput = React.memo(({ field, value, error, onChange, onSubmit }: OptionWithChildInputProps) => {

    const parentOptions = useParentOptions(field);
    const childOptions = useChildOptions(field, value);
    const findOptionById = useFindOptionById(field);

    const onParentChange = (selectedId: string | null) => {
        const newParent = findOptionById(selectedId);
        onChange(newParent);
        onSubmit(newParent);
    };

    const onChildChange = (selectedChildId: string | null) => {
        if (!value) return;

        const newChild = findOptionById(selectedChildId);
        const newValue = {
            ...value,
            child: newChild
        };

        onChange(newValue);
        onSubmit(newValue);
    };

    return <Flexer flex="column" gap=".5">
        <Select
            label={field.name}
            placeholder={`Select ${field.name.toLowerCase()}`}
            required={field.required && !field.hasDefaultValue}
            value={value?.id || null}
            onChange={onParentChange}
            data={parentOptions}
            clearable={!field.required}
            error={error}
        />
        {value && childOptions.length > 0 && (
            <Select
                placeholder={`Select sub-option for ${value.value}`}
                value={value.child?.id || null}
                onChange={onChildChange}
                data={childOptions}
                clearable
                error={error}
            />
        )}
    </Flexer>;
});
